import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Users,
  Car,
  Bus,
  Clock,
  MapPin,
  Plane,
  Building,
  Castle,
  CheckCircle,
  Phone,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Icon mapping for locations
const getLocationIcon = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes("airport") || lowerName.includes("cdg") || lowerName.includes("ory") || lowerName.includes("bva")) {
    return Plane;
  }
  if (lowerName.includes("disneyland") || lowerName.includes("versailles") || lowerName.includes("castle")) {
    return Castle;
  }
  return Building;
};

// Location Card Component with passenger-based pricing
const LocationCard = ({ location, tripType }) => {
  const [expanded, setExpanded] = useState(null);
  const IconComponent = getLocationIcon(location.pickup_location);
  
  // Get min prices for Car (1-3 pax) and Van (4-8 pax)
  const getMinPrices = (prices) => {
    const carPrices = prices.slice(0, 3).filter(p => p > 0);
    const vanPrices = prices.slice(3, 8).filter(p => p > 0);
    return {
      carMin: carPrices.length > 0 ? Math.min(...carPrices) : null,
      vanMin: vanPrices.length > 0 ? Math.min(...vanPrices) : null,
    };
  };

  // Check if route has valid passenger prices
  const hasValidPrices = (prices) => {
    return prices.some(p => p > 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden"
    >
      {/* Card Header */}
      <div className="bg-midnight-navy p-4 md:p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-champagne-gold/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <IconComponent className="w-5 h-5 md:w-6 md:h-6 text-champagne-gold" />
          </div>
          <div>
            <h3 className="font-heading font-bold text-lg md:text-xl text-white">
              {location.pickup_location}
            </h3>
            <p className="text-paris-stone/70 text-sm">
              {location.routes.filter(r => hasValidPrices(r.prices)).length} destinations available
            </p>
          </div>
        </div>
      </div>

      {/* Routes List */}
      <div className="divide-y divide-slate-100">
        {location.routes.filter(r => hasValidPrices(r.prices)).map((route, index) => {
          const { carMin, vanMin } = getMinPrices(route.prices);
          const isExpanded = expanded === index;
          
          return (
            <div key={index} className="hover:bg-slate-50 transition-colors">
              {/* Main Row - Always Visible */}
              <div className="p-4">
                {/* Mobile Layout */}
                <div className="md:hidden">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <MapPin className="w-4 h-4 text-champagne-gold flex-shrink-0" />
                      <span className="font-medium text-midnight-navy truncate">{route.dropoff_location}</span>
                    </div>
                    <button 
                      onClick={() => setExpanded(isExpanded ? null : index)}
                      className="p-1 text-slate-400 hover:text-slate-600"
                      aria-label="Toggle price details"
                    >
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex gap-2">
                      {carMin && (
                        <div className="flex items-center gap-1 bg-slate-100 px-2 py-1 rounded-lg">
                          <Car className="w-3 h-3 text-slate-600" />
                          <span className="text-xs text-slate-500">from</span>
                          <span className="font-bold text-midnight-navy">€{carMin}</span>
                        </div>
                      )}
                      {vanMin && (
                        <div className="flex items-center gap-1 bg-champagne-gold/10 px-2 py-1 rounded-lg">
                          <Bus className="w-3 h-3 text-champagne-gold" />
                          <span className="text-xs text-slate-500">from</span>
                          <span className="font-bold text-midnight-navy">€{vanMin}</span>
                        </div>
                      )}
                    </div>
                    <Button
                      asChild
                      size="sm"
                      className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-semibold rounded-full px-4 min-h-[44px]"
                    >
                      <Link
                        to={`/booking?tripType=${tripType === 'roundtrip' ? 'return' : 'oneway'}&pickup=${encodeURIComponent(location.pickup_location)}&dropoff=${encodeURIComponent(route.dropoff_location)}`}
                      >
                        Book
                      </Link>
                    </Button>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden md:flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <MapPin className="w-4 h-4 text-champagne-gold flex-shrink-0" />
                    <span className="font-medium text-midnight-navy">{route.dropoff_location}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    {carMin && (
                      <div className="flex items-center gap-1 bg-slate-100 px-3 py-1.5 rounded-lg">
                        <Car className="w-4 h-4 text-slate-600" />
                        <span className="text-xs text-slate-500">Car from</span>
                        <span className="font-heading font-bold text-lg text-midnight-navy">€{carMin}</span>
                      </div>
                    )}
                    {vanMin && (
                      <div className="flex items-center gap-1 bg-champagne-gold/10 px-3 py-1.5 rounded-lg">
                        <Bus className="w-4 h-4 text-champagne-gold" />
                        <span className="text-xs text-slate-500">Van from</span>
                        <span className="font-heading font-bold text-lg text-midnight-navy">€{vanMin}</span>
                      </div>
                    )}
                    <button 
                      onClick={() => setExpanded(isExpanded ? null : index)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                      aria-label="Toggle price details"
                    >
                      {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                    <Button
                      asChild
                      size="sm"
                      className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-semibold rounded-full px-5"
                    >
                      <Link
                        to={`/booking?tripType=${tripType === 'roundtrip' ? 'return' : 'oneway'}&pickup=${encodeURIComponent(location.pickup_location)}&dropoff=${encodeURIComponent(route.dropoff_location)}`}
                      >
                        Book Now
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Expanded Passenger Pricing */}
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="px-4 pb-4"
                >
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="text-xs text-slate-500 mb-3 font-medium uppercase tracking-wider">
                      Price by passenger count
                    </p>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                      {route.prices.map((price, paxIndex) => (
                        <div 
                          key={paxIndex} 
                          className={`text-center p-2 rounded-lg ${
                            paxIndex < 3 
                              ? 'bg-white border border-slate-200' 
                              : 'bg-champagne-gold/10 border border-champagne-gold/20'
                          } ${price === 0 ? 'opacity-40' : ''}`}
                        >
                          <div className="text-xs text-slate-500 mb-1 flex items-center justify-center gap-1">
                            {paxIndex < 3 ? (
                              <Car className="w-3 h-3" />
                            ) : (
                              <Bus className="w-3 h-3" />
                            )}
                            {paxIndex + 1}
                          </div>
                          <div className={`font-bold text-sm ${price === 0 ? 'text-slate-400' : 'text-midnight-navy'}`}>
                            {price === 0 ? '—' : `€${price}`}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Car className="w-3 h-3" /> Car: 1-3 passengers
                      </span>
                      <span className="flex items-center gap-1">
                        <Bus className="w-3 h-3" /> Van: 4-8 passengers
                      </span>
                    </div>
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>

      {/* Card Footer */}
      <div className="bg-slate-50 p-3 md:p-4">
        <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-slate-500">
          <span className="flex items-center gap-1">
            <Car className="w-3 h-3" /> Car: 1-3 pax
          </span>
          <span className="flex items-center gap-1">
            <Bus className="w-3 h-3" /> Van: 4-8 pax
          </span>
          <span className="text-slate-400">• Click arrow for full pricing</span>
        </div>
      </div>
    </motion.div>
  );
};

const Pricing = () => {
  const [activeTab, setActiveTab] = useState("oneway");
  const [loading, setLoading] = useState(true);
  const [onewayLocations, setOnewayLocations] = useState([]);
  const [roundtripLocations, setRoundtripLocations] = useState([]);
  const [hourlyRates, setHourlyRates] = useState({ car_rate: 60, van_rate: 70 });

  // Fetch rates from API (same source as admin dashboard)
  useEffect(() => {
    const fetchRates = async () => {
      try {
        // Fetch one-way rates
        const onewayRes = await fetch(`${API_URL}/api/pricing/routes?trip_type=oneway`);
        const roundtripRes = await fetch(`${API_URL}/api/pricing/routes?trip_type=return`);
        const hourlyRes = await fetch(`${API_URL}/api/pricing/hourly`);

        if (onewayRes.ok && roundtripRes.ok && hourlyRes.ok) {
          const onewayData = await onewayRes.json();
          const roundtripData = await roundtripRes.json();
          const hourlyData = await hourlyRes.json();

          // Group routes by pickup location
          const groupByPickup = (routes) => {
            const grouped = {};
            routes.forEach(route => {
              const pickup = route.pickup;
              if (!grouped[pickup]) {
                grouped[pickup] = {
                  pickup_location: pickup,
                  routes: []
                };
              }
              grouped[pickup].routes.push({
                dropoff_location: route.destination,
                prices: Object.values(route.prices)
              });
            });
            return Object.values(grouped);
          };

          setOnewayLocations(groupByPickup(onewayData.routes || []));
          setRoundtripLocations(groupByPickup(roundtripData.routes || []));
          setHourlyRates({
            car_rate: hourlyData.rate_per_hour_car || 60,
            van_rate: hourlyData.rate_per_hour_van || 70
          });
        }
      } catch (error) {
        console.error("Error fetching rates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();
  }, []);

  return (
    <div className="min-h-screen bg-paris-stone" data-testid="pricing-page">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-midnight-navy pt-28 pb-12 md:pt-32 md:pb-16">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
              Transparent <span className="text-champagne-gold">Pricing</span>
            </h1>
            <p className="text-base md:text-lg text-paris-stone/80 max-w-2xl mx-auto mb-6">
              Fixed prices with no hidden fees. Pay your driver directly on arrival.
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-4 md:gap-6">
              <div className="flex items-center gap-2 text-white text-sm">
                <CheckCircle className="w-4 h-4 text-champagne-gold" />
                <span>No Hidden Fees</span>
              </div>
              <div className="flex items-center gap-2 text-white text-sm">
                <CheckCircle className="w-4 h-4 text-champagne-gold" />
                <span>Pay Driver on Arrival</span>
              </div>
              <div className="flex items-center gap-2 text-white text-sm">
                <CheckCircle className="w-4 h-4 text-champagne-gold" />
                <span>Free Child Seats</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Content */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-white p-1.5 rounded-xl shadow-md">
                <TabsTrigger
                  value="oneway"
                  className="px-4 md:px-6 py-2.5 rounded-lg font-semibold data-[state=active]:bg-midnight-navy data-[state=active]:text-white text-slate-600"
                  data-testid="tab-oneway"
                >
                  One-Way
                </TabsTrigger>
                <TabsTrigger
                  value="roundtrip"
                  className="px-4 md:px-6 py-2.5 rounded-lg font-semibold data-[state=active]:bg-midnight-navy data-[state=active]:text-white text-slate-600"
                  data-testid="tab-roundtrip"
                >
                  Round Trip
                </TabsTrigger>
                <TabsTrigger
                  value="hourly"
                  className="px-4 md:px-6 py-2.5 rounded-lg font-semibold data-[state=active]:bg-midnight-navy data-[state=active]:text-white text-slate-600"
                  data-testid="tab-hourly"
                >
                  Hourly
                </TabsTrigger>
              </TabsList>
            </div>

            {/* One-Way Tab */}
            <TabsContent value="oneway">
              {loading ? (
                <div className="text-center py-12 text-slate-500">Loading prices...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {onewayLocations.map((location, index) => (
                    <LocationCard key={index} location={location} tripType="oneway" />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Round Trip Tab */}
            <TabsContent value="roundtrip">
              {loading ? (
                <div className="text-center py-12 text-slate-500">Loading prices...</div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {roundtripLocations.map((location, index) => (
                    <LocationCard key={index} location={location} tripType="roundtrip" />
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Hourly Tab */}
            <TabsContent value="hourly">
              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {/* Car Hourly */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-slate-200"
                  >
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-eiffel-blue/10 rounded-xl flex items-center justify-center">
                        <Car className="w-7 h-7 text-eiffel-blue" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-xl text-midnight-navy">Sedan Car</h3>
                        <p className="text-slate-600 text-sm">1-3 passengers</p>
                      </div>
                    </div>
                    <div className="text-center mb-6">
                      <span className="font-heading font-bold text-5xl text-champagne-gold">€{hourlyRates.car_rate}</span>
                      <span className="text-slate-600 text-lg">/hour</span>
                    </div>
                    <ul className="space-y-3 text-sm text-slate-600 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>30km included per hour</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Minimum 2 hours booking</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Professional English-speaking driver</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Free cancellation 24h before</span>
                      </li>
                    </ul>
                    <Button
                      asChild
                      className="w-full bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold rounded-full py-4 min-h-[48px]"
                    >
                      <Link to="/booking?tripType=hourly">
                        Book Sedan Tour
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </motion.div>

                  {/* Van Hourly */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border-2 border-champagne-gold relative"
                  >
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-champagne-gold text-midnight-navy text-xs font-bold px-3 py-1 rounded-full">
                        GROUPS
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-champagne-gold/10 rounded-xl flex items-center justify-center">
                        <Bus className="w-7 h-7 text-champagne-gold" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-xl text-midnight-navy">Spacious Van</h3>
                        <p className="text-slate-600 text-sm">4-8 passengers</p>
                      </div>
                    </div>
                    <div className="text-center mb-6">
                      <span className="font-heading font-bold text-5xl text-champagne-gold">€{hourlyRates.van_rate}</span>
                      <span className="text-slate-600 text-lg">/hour</span>
                    </div>
                    <ul className="space-y-3 text-sm text-slate-600 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>30km included per hour</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Minimum 2 hours booking</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Perfect for families & groups</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>Extra luggage space</span>
                      </li>
                    </ul>
                    <Button
                      asChild
                      className="w-full bg-midnight-navy hover:bg-midnight-navy/90 text-white font-bold rounded-full py-4 min-h-[48px]"
                    >
                      <Link to="/booking?tripType=hourly">
                        Book Van Tour
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Link>
                    </Button>
                  </motion.div>
                </div>

                {/* Popular Tour Packages */}
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="font-heading font-bold text-xl text-midnight-navy mb-4 text-center">
                    Popular Tour Packages
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { name: "Paris Highlights", hours: 4, price: hourlyRates.car_rate * 4 },
                      { name: "Versailles Day Trip", hours: 5, price: hourlyRates.car_rate * 5 },
                      { name: "Mont Saint-Michel", hours: 10, price: hourlyRates.car_rate * 10 },
                      { name: "Normandy & D-Day", hours: 10, price: hourlyRates.car_rate * 10 },
                    ].map((tour, index) => (
                      <div key={index} className="bg-paris-stone rounded-xl p-4 text-center">
                        <h4 className="font-semibold text-midnight-navy mb-1">{tour.name}</h4>
                        <p className="text-xs text-slate-500 mb-2">{tour.hours} hours</p>
                        <p className="font-heading font-bold text-lg text-champagne-gold mb-2">
                          From €{tour.price}
                        </p>
                        <Button
                          asChild
                          size="sm"
                          className="w-full bg-midnight-navy hover:bg-midnight-navy/90 text-white rounded-full min-h-[40px]"
                        >
                          <Link to="/booking?tripType=hourly">Book</Link>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Trust Badge */}
          <div className="mt-10 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-green-50 text-green-700 rounded-full">
              <span className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
              <span className="font-medium text-sm md:text-base">Pay Driver on Arrival - No prepayment required</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-midnight-navy">
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            Need a Custom Quote?
          </h2>
          <p className="text-paris-stone/80 mb-6 max-w-xl mx-auto">
            Contact us for special routes, group bookings over 8 passengers, or long-term arrangements.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button
              asChild
              size="lg"
              className="bg-champagne-gold hover:bg-champagne-gold/90 text-midnight-navy font-bold px-8 rounded-full min-h-[48px]"
            >
              <Link to="/contact">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white hover:text-midnight-navy font-semibold px-8 rounded-full min-h-[48px]"
            >
              <a href="tel:+33763519524">
                <Phone className="w-4 h-4 mr-2" />
                +33 763 519 524
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
